import { FC, useState } from 'react';
import {
    Box, Card, CardContent, Chip, Grid, List, ListItem, ListItemText,
    Skeleton, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useActivityDashboard } from '../../hooks/useActivityDashboard';
import { KpiCard } from '../analytics/common/KpiCard';
import { StatsDateFilter } from '../analytics/common/StatsDateFilter';
import { StatsLineChart } from '../analytics/common/StatsLineChart';
import { ContentFunnelCard } from './ContentFunnelCard';

const CHANGE_LABEL = '직전 대비';

const SectionTitle: FC<{ children: React.ReactNode; to?: string }> = ({ children, to }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {children}
        </Typography>
        {to && (
            <Typography component={RouterLink} to={to} variant="caption"
                sx={{ color: 'primary.main', textDecoration: 'none' }}>
                상세 →
            </Typography>
        )}
    </Box>
);

export const DashboardHome: FC = () => {
    const [range, setRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const { data, isLoading } = useActivityDashboard(range.start, range.end);

    return (
        <Box p={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight="bold">활동 대시보드</Typography>
                <StatsDateFilter defaultDays={7} onChange={(start, end) => setRange({ start, end })} />
            </Box>

            {isLoading || !data ? (
                <Grid container spacing={2} mt={1}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                            <Skeleton variant="rounded" height={110} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <>
                    {/* 참여 KPI */}
                    <SectionTitle to="/analytics/user-activity">사용자 참여</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="DAU" value={data.engagementKpi.dau} color="blue"
                                changePercent={data.engagementKpi.dauChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="WAU" value={data.engagementKpi.wau} color="blue"
                                changePercent={data.engagementKpi.wauChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="MAU" value={data.engagementKpi.mau} color="blue"
                                changePercent={data.engagementKpi.mauChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="활성률 (DAU/MAU)" value={`${data.engagementKpi.stickiness.toFixed(1)}%`}
                                color="purple" subtitle="고착도" />
                        </Grid>
                    </Grid>

                    {/* 학습량 KPI */}
                    <SectionTitle to="/analytics/learning">학습량</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="신규 가입" value={data.learningVolumeKpi.newSignups} color="green"
                                changePercent={data.learningVolumeKpi.signupChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="생성 단어장" value={data.learningVolumeKpi.createdWordbooks} color="green"
                                subtitle="직접 생성(PERSONAL)"
                                changePercent={data.learningVolumeKpi.createdChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="학습 세션" value={data.learningVolumeKpi.learningSessions} color="orange"
                                changePercent={data.learningVolumeKpi.sessionChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard title="학습된 단어" value={data.learningVolumeKpi.learnedWords} color="orange"
                                changePercent={data.learningVolumeKpi.learnedChange} changeLabel={CHANGE_LABEL} />
                        </Grid>
                    </Grid>

                    {/* 미니 트렌드 */}
                    <SectionTitle>추이</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card><CardContent>
                                <Typography variant="caption" color="text.secondary">DAU 추이</Typography>
                                <StatsLineChart height={220} xAxisKey="date"
                                    data={(data.dauTrend ?? []) as unknown as Record<string, unknown>[]}
                                    series={[{ dataKey: 'count', name: 'DAU', color: '#2563eb' }]} />
                            </CardContent></Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card><CardContent>
                                <Typography variant="caption" color="text.secondary">학습 세션 추이</Typography>
                                <StatsLineChart height={220} xAxisKey="date"
                                    data={(data.sessionTrend ?? []) as unknown as Record<string, unknown>[]}
                                    series={[{ dataKey: 'count', name: '세션', color: '#d97706' }]} />
                            </CardContent></Card>
                        </Grid>
                    </Grid>

                    {/* 활동 ↔ 콘텐츠 연결 */}
                    <SectionTitle to="/analytics/learning">활동 ↔ 학습 콘텐츠</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <ContentFunnelCard funnel={data.funnel} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Card sx={{ height: '100%' }}><CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>신규 가입 → 첫 학습</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#16a34a' }}>
                                    {data.activation.activationRate.toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    신규 {data.activation.newSignups.toLocaleString()}명 중 {data.activation.activated.toLocaleString()}명 첫 학습
                                </Typography>
                            </CardContent></Card>
                        </Grid>
                    </Grid>

                    {/* 활동 강도 */}
                    <SectionTitle>활동 강도</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard title="활성 유저당 세션" value={data.intensity.sessionsPerActiveUser.toFixed(2)} color="blue" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard title="학습자당 단어" value={data.intensity.wordsPerLearner.toFixed(1)} color="orange" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <KpiCard title="단어장 평균 완료율" value={`${data.intensity.avgCompletionRate.toFixed(1)}%`} color="green" />
                        </Grid>
                    </Grid>

                    {/* Top 랭킹 */}
                    <SectionTitle to="/library/stats">Top 콘텐츠</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card><CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Top 라이브러리 (다운로드)</Typography>
                                <List dense>
                                    {(data.topContent.topLibraries ?? []).map((lib, i) => (
                                        <ListItem key={i} disableGutters
                                            secondaryAction={<Typography variant="body2">{lib.downloads.toLocaleString()}</Typography>}>
                                            <ListItemText primary={`${i + 1}. ${lib.title}`} />
                                        </ListItem>
                                    ))}
                                    {(data.topContent.topLibraries ?? []).length === 0 && (
                                        <Typography variant="body2" color="text.secondary">데이터 없음</Typography>
                                    )}
                                </List>
                            </CardContent></Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card><CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Top 학습 단어장 (세션수)</Typography>
                                <List dense>
                                    {(data.topContent.topStudiedWordbooks ?? []).map((wb, i) => (
                                        <ListItem key={wb.wordbookId} disableGutters
                                            secondaryAction={<Typography variant="body2">{wb.sessionCount.toLocaleString()}회</Typography>}>
                                            <ListItemText primary={`${i + 1}. ${wb.title}`}
                                                secondary={`학습자 ${wb.learnerCount.toLocaleString()}명`} />
                                        </ListItem>
                                    ))}
                                    {(data.topContent.topStudiedWordbooks ?? []).length === 0 && (
                                        <Typography variant="body2" color="text.secondary">데이터 없음</Typography>
                                    )}
                                </List>
                            </CardContent></Card>
                        </Grid>
                    </Grid>

                    {/* 주의 필요 (운영) */}
                    <SectionTitle>⚠ 주의 필요</SectionTitle>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Chip component={RouterLink} to="/reports" clickable
                            color={data.alerts.pendingReports > 0 ? 'error' : 'default'}
                            label={`대기 신고 ${data.alerts.pendingReports}`} />
                        <Chip component={RouterLink} to="/inquiries" clickable
                            color={data.alerts.pendingInquiries > 0 ? 'warning' : 'default'}
                            label={`대기 문의 ${data.alerts.pendingInquiries}`} />
                        <Chip component={RouterLink} to="/batch/jobs" clickable
                            color={data.alerts.failedBatches > 0 ? 'error' : 'default'}
                            label={`실패 배치 ${data.alerts.failedBatches}`} />
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" mt={3}>
                        * 1분마다 자동 갱신됩니다
                    </Typography>
                </>
            )}
        </Box>
    );
};
